import { useEffect, useState, useSyncExternalStore } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const SYSTEM_FONTS = [
  { id: 'Inter', label: 'Inter', stack: 'Inter, sans-serif' },
  { id: 'Arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { id: 'Helvetica', label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
  { id: 'Georgia', label: 'Georgia', stack: 'Georgia, serif' },
  { id: 'Times New Roman', label: 'Times New Roman', stack: '"Times New Roman", Times, serif' },
  { id: 'Verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { id: 'Courier New', label: 'Courier New', stack: '"Courier New", Courier, monospace' },
  { id: 'Impact', label: 'Impact', stack: 'Impact, Haettenschweiler, sans-serif' },
  { id: 'Comic Sans MS', label: 'Comic Sans MS', stack: '"Comic Sans MS", cursive' },
];

export const FONT_FILE_ACCEPT = '.ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2';

let customFonts = [];
let brandFonts = [];
let fontsVersion = 0;
const listeners = new Set();

const emit = () => {
  fontsVersion += 1;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getCustomFontsSnapshot = () => customFonts;

const getBrandFontsSnapshot = () => brandFonts;

export const BRAND_FONT_ID_PREFIX = 'brand:';

export const setBrandFonts = (fonts) => {
  brandFonts = fonts;
  emit();
};

const fontLabelFromFile = (fileName) =>
  String(fileName || 'Font')
    .replace(/\.(ttf|otf|woff2?|eot)$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim() || 'Font';

export const addCustomFont = async (file) => {
  const id = `custom:${uuidv4()}`;
  const family = `ms-font-${id.slice(7)}`;
  const face = new FontFace(family, await file.arrayBuffer());
  await face.load();
  document.fonts.add(face);

  const baseLabel = fontLabelFromFile(file.name);
  const taken = new Set([...SYSTEM_FONTS, ...customFonts].map((font) => font.label));
  let label = baseLabel;
  for (let index = 2; taken.has(label); index += 1) {
    label = `${baseLabel} (${index})`;
  }

  const font = { id, label, stack: `"${family}", sans-serif`, face, custom: true };
  customFonts = [...customFonts, font];
  emit();
  return font;
};

export const removeCustomFont = (id) => {
  const font = customFonts.find((item) => item.id === id);
  if (!font) {
    return;
  }
  document.fonts.delete(font.face);
  customFonts = customFonts.filter((item) => item.id !== id);
  emit();
};

const getAllFonts = () => [...brandFonts, ...customFonts, ...SYSTEM_FONTS];

export const getFontStack = (fontId) => {
  const match = getAllFonts().find((font) => font.id === fontId || font.label === fontId);
  if (match) {
    return match.stack;
  }
  if (!fontId || String(fontId).startsWith('custom:') || String(fontId).startsWith(BRAND_FONT_ID_PREFIX)) {
    return SYSTEM_FONTS[0].stack;
  }
  return `"${fontId}", sans-serif`;
};

export const getFontLabel = (fontId) => getAllFonts().find((font) => font.id === fontId)?.label || fontId || '';

export const useCustomFonts = () => useSyncExternalStore(subscribe, getCustomFontsSnapshot);

export const useBrandFonts = () => useSyncExternalStore(subscribe, getBrandFontsSnapshot);

export const useFontsVersion = () => {
  const registryVersion = useSyncExternalStore(subscribe, () => fontsVersion);
  const [loadedVersion, setLoadedVersion] = useState(0);

  useEffect(() => {
    if (!document.fonts) {
      return undefined;
    }
    let cancelled = false;
    const bump = () => {
      if (!cancelled) {
        setLoadedVersion((value) => value + 1);
      }
    };
    document.fonts.ready.then(bump).catch(() => {});
    document.fonts.addEventListener?.('loadingdone', bump);
    return () => {
      cancelled = true;
      document.fonts.removeEventListener?.('loadingdone', bump);
    };
  }, []);

  return `${registryVersion}:${loadedVersion}`;
};

export const DEFAULT_TEXT_SHADOW = {
  enabled: false,
  color: '#000000',
  blur: 8,
  offsetX: 2,
  offsetY: 2,
  opacity: 0.5,
};

export const DEFAULT_TEXT_OUTLINE = {
  enabled: false,
  color: '#000000',
  width: 2,
};

export const DEFAULT_TEXT_BACKGROUND = {
  enabled: false,
  fill: '#FDBF47',
  padding: 8,
  cornerRadius: 4,
  opacity: 1,
  blur: 0,
};

const getDocShortSide = (docWidth, docHeight) => Math.min(docWidth || 0, docHeight || 0) || 1080;

export const getScaledTextDefaults = (docWidth, docHeight, sizeRatio = 0.06) => {
  const shortSide = getDocShortSide(docWidth, docHeight);
  const fontSize = Math.max(8, Math.round(shortSide * sizeRatio));
  const margin = Math.max(24, Math.round(shortSide * 0.04));
  const width = Math.max(120, Math.min(docWidth * 0.7, (docWidth || shortSide) - margin * 2));
  return { fontSize, width };
};
